import React from "react";
import { Link, Typography, ViewDescription } from "@sumprio/ui";
import {
  textNeedsTableDescriptionExpand,
  type TextNeedsTableDescriptionExpandOptions,
} from "./textNeedsTableDescriptionExpand";

export interface TableTruncatedDescriptionLinkConfig {
  href?: string;
  onClick?: (_event: React.MouseEvent<HTMLAnchorElement>) => void;
  ariaLabel?: string;
}

export interface TableTruncatedDescriptionProps {
  text: string | null | undefined;
  toggleAriaLabel: string;
  viewDescriptionClassName?: string;
  plainTextLineClamp?: number;
  expandHeuristicOptions?: TextNeedsTableDescriptionExpandOptions;
  link?: TableTruncatedDescriptionLinkConfig | null;
}

const DEFAULT_VIEW_DESCRIPTION_CLASS = "table-cell__view-description";

function renderLinkedOrPlainText(
  raw: string,
  link?: TableTruncatedDescriptionLinkConfig | null
): React.ReactNode {
  if (!link) return raw;

  return (
    <Link
      href={link.href ?? "#"}
      size="sm"
      underline={false}
      aria-label={link.ariaLabel}
      onClick={(event) => {
        link.onClick?.(event);
      }}
    >
      {raw}
    </Link>
  );
}

export default function TableTruncatedDescription({
  text,
  toggleAriaLabel,
  viewDescriptionClassName = DEFAULT_VIEW_DESCRIPTION_CLASS,
  plainTextLineClamp,
  expandHeuristicOptions,
  link = null,
}: TableTruncatedDescriptionProps) {
  const raw = text ?? "";
  const trimmed = raw.trim();
  const content = renderLinkedOrPlainText(raw, link);

  if (!trimmed) {
    return (
      <Typography variant="body" bodySize="sm" bodyWeight="regular">
        —
      </Typography>
    );
  }

  if (textNeedsTableDescriptionExpand(raw, expandHeuristicOptions)) {
    return (
      <ViewDescription className={viewDescriptionClassName} toggleAriaLabel={toggleAriaLabel}>
        {content}
      </ViewDescription>
    );
  }

  return (
    <Typography
      variant="body"
      bodySize="sm"
      bodyWeight="regular"
      {...(plainTextLineClamp !== undefined ? { lineClamp: plainTextLineClamp } : {})}
    >
      {content}
    </Typography>
  );
}
